import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { adminApi } from '../../utils/api';
import Button from './shared/Button';
import Input from './shared/Input';
import Select from './shared/Select';
import Textarea from './shared/Textarea';
import Card from './shared/Card';
import Alert from './shared/Alert';
import Badge from './shared/Badge';
import InfoRow from './shared/InfoRow';
import Spinner from './shared/Spinner';
import Tabs from './shared/Tabs';

const STATUS_BADGE = {
    indexed: 'success',
    pending: 'warning',
    failed: 'danger',
};

const VECTOR_STORE_OPTIONS = [
    { value: 'qdrant', label: 'Qdrant' },
    { value: 's3', label: 'S3 Vectors' },
    { value: 'gemini_file_search', label: 'Gemini File Search Store' },
];

const EMBEDDING_PROVIDER_OPTIONS = [
    { value: 'sentence_transformer', label: 'SentenceTransformer (Local)' },
    { value: 'gemini', label: 'Gemini (Google API)' },
    { value: 'openai', label: 'OpenAI' },
];

const DEVICE_OPTIONS = [
    { value: 'cpu', label: 'CPU' },
    { value: 'cuda', label: 'CUDA (GPU)' },
    { value: 'mps', label: 'MPS (Apple Silicon)' },
];

const CHUNK_STRATEGY_OPTIONS = [
    { value: 'recursive', label: 'Recursive (character-based)' },
    { value: 'token', label: 'Token (tiktoken-based)' },
];

const RERANKER_OPTIONS = [
    { value: 'none', label: 'None' },
    { value: 'cross_encoder', label: 'Cross-Encoder (local model)' },
    { value: 'gemini', label: 'Gemini (API-based)' },
];

const RERANKER_MODEL_OPTIONS = [
    { value: 'AITeamVN/Vietnamese_Reranker', label: 'Vietnamese Reranker (bge-reranker-v2-m3 fine-tuned)' },
    { value: 'BAAI/bge-reranker-v2-m3', label: 'BGE Reranker v2 M3 (multilingual)' },
];

const ALL_MODELS = [
    { value: 'AITeamVN/Vietnamese_Embedding_v2', label: 'Vietnamese Embedding v2 (1024d, Vietnamese)', provider: 'sentence_transformer', dims: 1024 },
    { value: 'BAAI/bge-m3', label: 'BAAI/bge-m3 (1024d, multilingual)', provider: 'sentence_transformer', dims: 1024 },
    { value: 'BAAI/bge-large-en-v1.5', label: 'BAAI/bge-large-en-v1.5 (1024d, English)', provider: 'sentence_transformer', dims: 1024 },
    { value: 'sentence-transformers/all-MiniLM-L6-v2', label: 'all-MiniLM-L6-v2 (384d, fast)', provider: 'sentence_transformer', dims: 384 },
    { value: 'gemini-embedding-001', label: 'gemini-embedding-001 (up to 3072d)', provider: 'gemini', dims: 768 },
    { value: 'text-embedding-3-small', label: 'OpenAI text-embedding-3-small (1536d)', provider: 'openai', dims: 1536 },
    { value: 'text-embedding-3-large', label: 'OpenAI text-embedding-3-large (3072d)', provider: 'openai', dims: 3072 },
];

export default function AdminRAGCollectionForm() {
    const { collectionId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEdit = location.pathname.endsWith('/edit');
    const isCreate = !collectionId;
    const isView = !!collectionId && !isEdit;

    const activeTab = location.pathname.endsWith('/info') ? 'info' : 'documents';

    const [data, setData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [docsLoading, setDocsLoading] = useState(false);
    const [reindexing, setReindexing] = useState(false);
    const [indexingDocId, setIndexingDocId] = useState(null);

    useEffect(() => { loadData(); }, [collectionId]);
    useEffect(() => { if (isView && collectionId) loadDocuments(); }, [isView, collectionId]);

    const loadData = async () => {
        try {
            setLoading(true);
            if (isCreate) {
                const initial = {
                    id: '', name: '', description: '',
                    vector_store: 's3',
                    embedding_provider: 'sentence_transformer',
                    embedding_model: 'BAAI/bge-m3',
                    embedding_device: 'cpu',
                    embedding_dimensions: null,
                    chunk_strategy: 'recursive',
                    chunk_size: 1000, chunk_overlap: 200,
                    vector_dimensions: 1024,
                    reranker_provider: 'none',
                    reranker_model: 'AITeamVN/Vietnamese_Reranker',
                    reranker_device: 'cpu',
                    score_threshold: null,
                };
                setData(initial);
                setFormData(initial);
            } else {
                const col = await adminApi.getRAGCollection(collectionId);
                setData(col);
                setFormData({ ...col });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadDocuments = async () => {
        try {
            setDocsLoading(true);
            const res = await adminApi.listRAGDocuments(collectionId);
            setDocuments(res.documents || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setDocsLoading(false);
        }
    };

    const toSlug = (str) =>
        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/đ/g, 'd').replace(/Đ/g, 'd')
            .toLowerCase().trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/[\s_]+/g, '-')
            .replace(/^_+|_+$/g, '');

    const handleChange = (key, value) => {
        if (key === 'name' && isCreate) {
            setFormData(prev => ({ ...prev, name: value, id: toSlug(value) }));
            return;
        }
        setFormData(prev => ({ ...prev, [key]: value }));
    };

    const handleModelChange = (model) => {
        const preset = ALL_MODELS.find(m => m.value === model);
        if (!preset) return;
        const supportsCustomDims = ['gemini', 'openai'].includes(preset.provider);
        setFormData(prev => ({
            ...prev,
            embedding_model: model,
            embedding_provider: preset.provider,
            embedding_dimensions: supportsCustomDims ? preset.dims : null,
            embedding_device: preset.provider === 'sentence_transformer' ? prev.embedding_device : 'cpu',
            vector_dimensions: preset.dims,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            if (isCreate) {
                const payload = { ...formData };
                if (payload.vector_store === 'gemini_file_search') {
                    payload.embedding_provider = 'sentence_transformer';
                    payload.embedding_model = 'BAAI/bge-m3';
                    payload.embedding_device = 'cpu';
                    payload.embedding_dimensions = null;
                    payload.chunk_strategy = 'recursive';
                    payload.chunk_size = 512;
                    payload.chunk_overlap = 50;
                    payload.vector_dimensions = 1024;
                    payload.reranker_provider = 'none';
                }
                await adminApi.createRAGCollection(payload);
                navigate(`/admin/rag/manage/${formData.id}`);
            } else {
                await adminApi.updateRAGCollection(collectionId, {
                    name: formData.name,
                    description: formData.description,
                    chunk_size: formData.chunk_size,
                    chunk_overlap: formData.chunk_overlap,
                });
                navigate(`/admin/rag/manage/${collectionId}`);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteDoc = async (e, docId, title) => {
        e.stopPropagation();
        if (!confirm(`Delete document "${title}"?`)) return;
        try {
            await adminApi.deleteRAGDocument(docId);
            setSuccessMessage(`Document "${title}" deleted`);
            loadDocuments();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleIndexDoc = async (e, docId) => {
        e.stopPropagation();
        setIndexingDocId(docId);
        try {
            const result = await adminApi.indexRAGDocument(docId);
            setSuccessMessage(`Indexed: ${result.chunks_indexed} chunks (${result.status})`);
            loadDocuments();
            loadData();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIndexingDocId(null);
        }
    };

    const handleReindexAll = async () => {
        if (!confirm(`Reindex all documents in this collection?`)) return;
        setReindexing(true);
        try {
            const results = await adminApi.reindexRAGCollection(collectionId);
            const totalChunks = results.reduce((sum, r) => sum + r.chunks_indexed, 0);
            setSuccessMessage(`Reindexed ${results.length} documents, ${totalChunks} total chunks`);
            loadDocuments();
            loadData();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        } finally {
            setReindexing(false);
        }
    };

    if (loading) return <Spinner size="lg" text="Loading..." center />;

    const title = isCreate ? 'Create Collection' : isEdit ? `Edit: ${data?.name}` : data?.name;

    const providerLabel = EMBEDDING_PROVIDER_OPTIONS.find(o => o.value === data?.embedding_provider)?.label || data?.embedding_provider;
    const storeLabel = VECTOR_STORE_OPTIONS.find(o => o.value === data?.vector_store)?.label || data?.vector_store;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link
                    to="/admin/rag/manage"
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </Link>
                <h1 className="text-lg font-semibold text-gray-900">{title}</h1>
                {isView && (
                    <div className="ml-auto flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/rag/manage/${collectionId}/edit`)}>
                            Edit
                        </Button>
                    </div>
                )}
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            {isView ? (
                <div className="space-y-4">
                    <Tabs
                        tabs={[
                            { id: 'documents', label: 'Documents', count: documents.length },
                            { id: 'info', label: 'Info' },
                        ]}
                        active={activeTab}
                        onChange={(tab) => navigate(`/admin/rag/manage/${collectionId}/${tab === 'documents' ? 'docs' : 'info'}`, { replace: true })}
                        className="mb-4"
                    />

                    {activeTab === 'info' && (
                        <Card title="Collection Details">
                            <InfoRow label="ID" value={data.id} mono />
                            <InfoRow label="Name" value={data.name} />
                            <InfoRow label="Description" value={data.description || '-'} />
                            <InfoRow label="Vector Store" value={storeLabel} />
                            <InfoRow label="Embedding Provider" value={providerLabel} />
                            <InfoRow label="Embedding Model" value={data.embedding_model} mono />
                            {data.embedding_provider === 'sentence_transformer' && (
                                <InfoRow label="Embedding Device" value={data.embedding_device} />
                            )}
                            {data.embedding_dimensions && (
                                <InfoRow label="Embedding Dimensions" value={data.embedding_dimensions} />
                            )}
                            <InfoRow label="Chunk Strategy" value={data.chunk_strategy} />
                            <InfoRow label="Chunk Size" value={data.chunk_size} />
                            <InfoRow label="Chunk Overlap" value={data.chunk_overlap} />
                            <InfoRow label="Vector Dimensions" value={data.vector_dimensions} />
                            {data.reranker_provider !== 'none' && (
                                <>
                                    <InfoRow label="Reranker" value={data.reranker_provider} />
                                    <InfoRow label="Reranker Model" value={data.reranker_model} mono />
                                </>
                            )}
                            {data.score_threshold != null && (
                                <InfoRow label="Score Threshold" value={data.score_threshold} />
                            )}
                            <InfoRow label="Documents" value={data.document_count} />
                            <InfoRow label="Status" value={
                                <Badge variant={data.is_active ? 'success' : 'default'} dot>
                                    {data.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                            } />
                        </Card>
                    )}

                    {activeTab === 'documents' && (
                        <Card
                            title="Documents"
                            headerRight={
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        onClick={handleReindexAll}
                                        loading={reindexing}
                                        disabled={documents.length === 0}
                                    >
                                        Reindex All
                                    </Button>
                                    <Button
                                        size="xs"
                                        onClick={() => navigate(`/admin/rag/manage/${collectionId}/docs/new`)}
                                    >
                                        Add Document
                                    </Button>
                                </div>
                            }
                            noPadding
                        >
                            {docsLoading ? (
                                <div className="p-4"><Spinner size="sm" text="Loading documents..." /></div>
                            ) : documents.length === 0 ? (
                                <div className="text-center py-8 text-gray-500 text-sm">
                                    No documents yet. Add one to get started.
                                </div>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-200 bg-gray-50/60">
                                            <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Title</th>
                                            <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Source</th>
                                            <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Status</th>
                                            <th className="text-center px-3 py-2.5 text-xs font-medium text-gray-500">Chunks</th>
                                            <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-36">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {documents.map((doc) => (
                                            <tr
                                                key={doc.id}
                                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                                onClick={() => navigate(`/admin/rag/docs/${doc.id}`)}
                                            >
                                                <td className="px-4 py-3">
                                                    <span className="font-medium text-gray-900">{doc.title}</span>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <Badge variant="outline">{doc.source_type}</Badge>
                                                </td>
                                                <td className="px-3 py-3 text-center">
                                                    <Badge variant={STATUS_BADGE[doc.status] || 'default'} dot>
                                                        {doc.status}
                                                    </Badge>
                                                </td>
                                                <td className="px-3 py-3 text-center text-gray-700">{doc.chunk_count}</td>
                                                <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                                                    <div className="flex items-center justify-end gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            onClick={(e) => handleIndexDoc(e, doc.id)}
                                                            loading={indexingDocId === doc.id}
                                                        >
                                                            Index
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="xs"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            onClick={(e) => handleDeleteDoc(e, doc.id, doc.title)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </Card>
                    )}
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <Card title="General">
                        <Input
                            label="Name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            placeholder="Collection Name"
                            required
                        />
                        {formData.id && (
                            <InfoRow label="ID" value={formData.id} mono />
                        )}
                        <Textarea
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder="What this collection contains..."
                            rows={3}
                        />
                    </Card>

                    {isCreate && (
                        <>
                            <Card title="Vector Store">
                                <Select
                                    label="Provider"
                                    value={formData.vector_store}
                                    onChange={(e) => handleChange('vector_store', e.target.value)}
                                    options={VECTOR_STORE_OPTIONS}
                                />
                                {formData.vector_store === 'gemini_file_search' && (
                                    <p className="text-sm text-gray-500 mt-2">
                                        Gemini File Search Store handles chunking, embedding, and indexing automatically. Upload files or URLs directly.
                                    </p>
                                )}
                            </Card>

                            {formData.vector_store !== 'gemini_file_search' && (
                            <Card title="Embedding Configuration">
                                <Select
                                    label="Model"
                                    value={formData.embedding_model}
                                    onChange={(e) => handleModelChange(e.target.value)}
                                    options={ALL_MODELS}
                                    hint={`Provider: ${EMBEDDING_PROVIDER_OPTIONS.find(o => o.value === formData.embedding_provider)?.label || formData.embedding_provider}`}
                                />
                                {formData.embedding_provider === 'sentence_transformer' && (
                                    <Select
                                        label="Device"
                                        value={formData.embedding_device}
                                        onChange={(e) => handleChange('embedding_device', e.target.value)}
                                        options={DEVICE_OPTIONS}
                                    />
                                )}
                                {['gemini', 'openai'].includes(formData.embedding_provider) && (
                                    <Input
                                        label="Output Dimensions"
                                        type="number"
                                        value={formData.embedding_dimensions || 768}
                                        onChange={(e) => {
                                            const dims = parseInt(e.target.value) || 768;
                                            setFormData(prev => ({
                                                ...prev,
                                                embedding_dimensions: dims,
                                                vector_dimensions: dims,
                                            }));
                                        }}
                                        hint={formData.embedding_provider === 'gemini' ? '128-3072' : '256-3072 (v3 models support truncation)'}
                                    />
                                )}
                                <Input
                                    label="Vector Dimensions"
                                    type="number"
                                    value={formData.vector_dimensions}
                                    onChange={(e) => handleChange('vector_dimensions', parseInt(e.target.value) || 1024)}
                                    hint="Auto-set from model, or override manually"
                                    disabled={formData.embedding_provider === 'gemini'}
                                />
                            </Card>
                            )}
                        </>
                    )}

                    {formData.vector_store !== 'gemini_file_search' && (
                    <Card title="Chunking">
                        <Select
                            label="Strategy"
                            value={formData.chunk_strategy}
                            onChange={(e) => handleChange('chunk_strategy', e.target.value)}
                            options={CHUNK_STRATEGY_OPTIONS}
                            hint={formData.chunk_strategy === 'token' ? 'Splits by token count (tiktoken)' : 'Splits by character count'}
                        />
                        <div className="grid grid-cols-2 gap-4">
                            <Input
                                label={formData.chunk_strategy === 'token' ? 'Chunk Tokens' : 'Chunk Size'}
                                type="number"
                                value={formData.chunk_size}
                                onChange={(e) => handleChange('chunk_size', parseInt(e.target.value) || 512)}
                                hint={formData.chunk_strategy === 'token' ? '64-1024 tokens' : '64-4096 chars'}
                            />
                            <Input
                                label="Overlap"
                                type="number"
                                value={formData.chunk_overlap}
                                onChange={(e) => handleChange('chunk_overlap', parseInt(e.target.value) || 50)}
                                hint="0-512"
                            />
                        </div>
                    </Card>
                    )}

                    {formData.vector_store !== 'gemini_file_search' && (
                    <Card title="Reranker">
                        <Select
                            label="Provider"
                            value={formData.reranker_provider}
                            onChange={(e) => handleChange('reranker_provider', e.target.value)}
                            options={RERANKER_OPTIONS}
                        />
                        {formData.reranker_provider === 'cross_encoder' && (
                            <>
                                <Select
                                    label="Model"
                                    value={formData.reranker_model}
                                    onChange={(e) => handleChange('reranker_model', e.target.value)}
                                    options={RERANKER_MODEL_OPTIONS}
                                />
                                <Select
                                    label="Device"
                                    value={formData.reranker_device}
                                    onChange={(e) => handleChange('reranker_device', e.target.value)}
                                    options={DEVICE_OPTIONS}
                                />
                            </>
                        )}
                        <Input
                            label="Score Threshold"
                            type="number"
                            value={formData.score_threshold || ''}
                            onChange={(e) => handleChange('score_threshold', e.target.value ? parseFloat(e.target.value) : null)}
                            placeholder="None (return all results)"
                            hint="0.0-1.0 — filter results below this score"
                        />
                    </Card>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 pb-4">
                        <Button variant="ghost" type="button" onClick={() => navigate(isCreate ? '/admin/rag/manage' : `/admin/rag/manage/${collectionId}`)}>
                            Cancel
                        </Button>
                        <Button type="submit" loading={saving}>
                            {isCreate ? 'Create Collection' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            )}
        </div>
    );
}
